// Admin web-push registration helpers.
// Push is guarded and safe: best-effort enhancement that never blocks the admin UI.
import { toast } from "sonner";
import { apiFetch } from "./api/http";
import { isIOS, isStandalone } from "./pwa";

const SW_PATH = "/push-sw.js";
const PUSH_CACHE = "seen-push";
const VAPID_KEY_URL = "/__seen_vapid_key__";

/** Persist the VAPID key so the service worker can re-subscribe on rotation. */
async function cacheVapidKey(key: string): Promise<void> {
  try {
    const cache = await caches.open(PUSH_CACHE);
    await cache.put(VAPID_KEY_URL, new Response(key, { headers: { "Content-Type": "text/plain" } }));
  } catch {
    /* cache unavailable */
  }
}

function pushSupported(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      window.self === window.top &&
      window.isSecureContext &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window
    );
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function sameKey(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export async function registerAdminPush(): Promise<void> {
  if (!pushSupported()) return;

  // iOS Safari requires web push apps to be installed to Home Screen and opened standalone.
  if (isIOS() && !isStandalone()) {
    toast("Enable notifications on iPhone", {
      description:
        'Open SEEN in Safari → tap Share → "Add to Home Screen", then open SEEN from the home-screen icon to receive order alerts.',
      duration: 12000,
    });
    return;
  }

  try {
    const permission =
      Notification.permission === "default"
        ? await Notification.requestPermission()
        : Notification.permission;
    if (permission !== "granted") return;

    const reg = await navigator.serviceWorker.register(SW_PATH);
    await navigator.serviceWorker.ready;

    // Fetch the server VAPID key
    const res = await apiFetch<{ publicKey: string }>("/push/public-key", { auth: false });
    const key = res?.publicKey;
    if (!key) return;

    await cacheVapidKey(key);

    let sub = await reg.pushManager.getSubscription();

    // If existing subscription used a different key, recreate it
    if (sub) {
      const current = sub.options?.applicationServerKey;
      const expected = urlBase64ToUint8Array(key);
      if (!current || !sameKey(new Uint8Array(current), expected)) {
        await sub.unsubscribe().catch(() => {});
        sub = null;
      }
    }

    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as unknown as ArrayBuffer,
      });
    }

    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

    // Register subscription with the backend
    await apiFetch("/push/subscribe", {
      method: "POST",
      body: {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      },
      auth: true,
    });
  } catch {
    /* best-effort */
  }
}

export async function refreshAdminPush(): Promise<void> {
  if (!pushSupported()) return;
  if (isIOS() && !isStandalone()) return;
  if (Notification.permission !== "granted") return;
  await registerAdminPush();
}

export async function unregisterAdminPush(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await apiFetch("/push/unsubscribe", {
        method: "POST",
        body: { endpoint: sub.endpoint },
        auth: true,
      }).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
  } catch {
    /* ignore */
  }
}
